import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import { OrganizationBubble } from '@/components/user/OrganizationBubble';
import { RoleBadge } from '@/components/user/RoleBadge';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import {
    ActiveRoleAssignmentResponse,
    getAllRoleAssignments,
    type RoleAssignmentsContainer,
} from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';
import { resetReactQueryAfterRoleSwitch } from '@/components/layout/OrganizationRoleSwitcher';

interface RoleAssignmentRow {
    role_assignment_id: number;
    role_assignment_uuid: string;
    role: string;
    organization_id: number | null;
    organization_name?: string;
    organization_status?: Status | null;
    status: Status;
    isActive: boolean;
    isDefault: boolean;
}

interface RoleAssignmentsTableProps {
    user: RoleAssignmentsContainer;
}

export function RoleAssignmentsTable({ user }: RoleAssignmentsTableProps) {
    const log = createComponentLogger('RoleAssignmentsTable');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showDeleted, setShowDeleted] = useState(false);
    const setActiveRoleAssignmentId: (activeRoleAssignmentId: number | null) => void = useAuthStore(
        (state) => state.setActiveRoleAssignmentId
    );

    const setActiveRoleAssignmentMutation = useMutation({
        mutationFn: authApi.setActiveRoleAssignment,
        onSuccess: (data) => {
            // Avoid carrying stale pkey state while the active-role query refetches.
            setActiveRoleAssignmentId(null);
            resetReactQueryAfterRoleSwitch(queryClient, log);
            if (data.requires_totp_setup) {
                runInBackground(
                    navigate(buildRequiredTotpSetupPath(data.totp_setup_activation_target)),
                    (err) => {
                        log.error('Error navigating to TOTP setup page', err);
                    }
                );
                return;
            }
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries', err);
            });
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['activeRoleAssignment'] }),
                (err) => {
                    log.error('Error invalidating active role assignment', err);
                }
            );
        },
    });

    const setDefaultRoleAssignmentMutation = useMutation({
        mutationFn: authApi.setDefaultRoleAssignment,
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries', err);
            });
        },
    });

    // Fetch active role assignment from current session
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!user,
        staleTime: 30000,
    });
    useEffect(() => {
        const nextActiveRoleAssignmentId: number | null =
            getAllRoleAssignments(user).find(
                (roleAssignment) =>
                    roleAssignment.role_assignment_uuid ===
                    activeRoleAssignment?.role_assignment_uuid
            )?.role_assignment_id ?? null;
        setActiveRoleAssignmentId(nextActiveRoleAssignmentId);
    }, [activeRoleAssignment?.role_assignment_uuid, setActiveRoleAssignmentId, user]);

    // Transform role_assignments into table rows with status flags
    const roleAssignments = getAllRoleAssignments(user);
    const rows: RoleAssignmentRow[] = roleAssignments.map((ra) => {
        if (ra.role_assignment_uuid === undefined) {
            throw new Error('role_assignment_uuid is required for role assignment operations');
        }
        return {
            role_assignment_id: ra.role_assignment_id,
            role_assignment_uuid: ra.role_assignment_uuid,
            role: ra.role,
            organization_id: ra.organization_id,
            ...(ra.organization_name && { organization_name: ra.organization_name }),
            organization_status: ra.organization_status ?? null,
            status: ra.status,
            isActive:
                activeRoleAssignment?.role_assignment_uuid === ra.role_assignment_uuid,
            isDefault: ra.role_assignment_id === user.default_role_assignment?.role_assignment_id,
        };
    });
    const rowsInAvailableOrganizations: RoleAssignmentRow[] = rows.filter(
        (row) => row.organization_status !== Status.DELETED
    );
    const hasDeletedRows: boolean = rowsInAvailableOrganizations.some(
        (row) => row.status === Status.DELETED
    );
    const visibleRows: RoleAssignmentRow[] = useMemo(() => {
        if (showDeleted) {
            return rowsInAvailableOrganizations;
        }
        return rowsInAvailableOrganizations.filter((row) => row.status !== Status.DELETED);
    }, [rowsInAvailableOrganizations, showDeleted]);

    return (
        <div className="space-y-2">
            {hasDeletedRows && (
                <div className="flex justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleted((currentValue) => !currentValue)}
                    >
                        {showDeleted ? 'Hide deleted' : 'Show deleted'}
                    </Button>
                </div>
            )}
            {visibleRows.map((row) => {
                const showSetActive: boolean =
                    row.status === Status.ACTIVE &&
                    row.organization_status !== Status.DISABLED &&
                    !row.isActive;
                const showSetDefault: boolean =
                    row.status === Status.ACTIVE &&
                    row.organization_status !== Status.DISABLED &&
                    !row.isDefault;

                return (
                    <div
                        key={row.role_assignment_id}
                        className="rounded border p-3 md:grid md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start md:gap-4"
                    >
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Organization</div>
                            {row.organization_id === null ? (
                                <span className="text-muted-foreground">-</span>
                            ) : (
                                <OrganizationBubble
                                    organizationName={row.organization_name || null}
                                    isDefault={row.isDefault}
                                />
                            )}
                        </div>
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Role</div>
                            <RoleBadge role={row.role} />
                        </div>
                        <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">Status</div>
                            <div className="flex flex-col gap-1">
                                <Badge variant={row.status === Status.DELETED ? 'destructive' : 'outline'}>
                                    {row.status === Status.ACTIVE
                                        ? 'Enabled'
                                        : row.status === Status.DISABLED
                                          ? 'Disabled'
                                          : row.status === Status.PENDING
                                            ? 'Pending'
                                            : 'Deleted'}
                                </Badge>
                                {row.isActive && (
                                    <Badge className="bg-blue-500 text-white hover:bg-blue-500">
                                        Session Active
                                    </Badge>
                                )}
                                {row.isDefault && (
                                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                                        Default
                                    </Badge>
                                )}
                                {row.organization_status === Status.DISABLED && (
                                    <Badge variant="outline">Organization disabled</Badge>
                                )}
                            </div>
                        </div>
                        <div className="mt-3 flex flex-col gap-1 md:mt-0">
                            {showSetActive && (
                                <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs bg-blue-500 hover:bg-blue-600 text-white"
                                    onClick={() => {
                                        setActiveRoleAssignmentMutation.mutate(
                                            row.role_assignment_uuid
                                        );
                                    }}
                                    disabled={setActiveRoleAssignmentMutation.isPending}
                                >
                                    Set active
                                </Button>
                            )}
                            {showSetDefault && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300"
                                    onClick={() => {
                                        setDefaultRoleAssignmentMutation.mutate(
                                            row.role_assignment_uuid
                                        );
                                    }}
                                    disabled={setDefaultRoleAssignmentMutation.isPending}
                                >
                                    Set default
                                </Button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
